import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,  
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  MenuItem,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import DefaultApi from '../../Api/DefaultApi';
import manageReportApi from '../../Api/ApiManageReportData';
import appdata from '../../Services/appdata';
import xero_logo from '../../Assets/image/xero.png';
import myob_logo from '../../Assets/image/myob1.png';
import sql_logo from '../../Assets/image/sql1.png';
import rapport_logo from '../../Assets/image/rapport1.png';
import workflow_max_logo from '../../Assets/image/workflow_max1.png';
import manual_entry_logo from '../../Assets/image/manual-icon.jpg';
import synergy_logo from '../../Assets/image/synergy.png';
import deltek_vp_logo from '../../Assets/image/deltek_vp.png';
import simplify_logo from '../../Assets/image/simplify.png';
import {
  xeroAccountLists,
  sqlAccountLists,
  myobAccountLists,
  rapportAccountLists,
  workflowAccountLists,
  manaualEntryAccountLists,
  synergyAccountLists,
  deltekVpAccountLists,
  simplifyAccountLists,
} from '../../Utils/constants';

const systemLists = [
  { text: 'Xero Accounting', src: xero_logo },
  { text: 'Sql Accounting', src: sql_logo },
  { text: 'Myob Accounting', src: myob_logo },
  { text: 'Rapport Accounting', src: rapport_logo },
  { text: 'Workflow ProjectManagement', src: workflow_max_logo },
  { text: 'Manual Entry', src: manual_entry_logo },
  { text: 'Synergy', src: synergy_logo },
  { text: 'Deltek', src: deltek_vp_logo },
  { text: 'Simplify', src: simplify_logo },
];

const emptySynergyProjectMappings = { director: null, source1: null, source2: null };

const synergyCustomFieldToken = (field) => {
  if (!field) return '';
  if (field.id !== null && field.id !== undefined && field.id !== '') return `id:${field.id}`;
  return field.name ? `name:${field.name}` : '';
};

const mergeSynergyCustomFieldOptions = (fields = [], mappings = {}) => {
  const merged = new Map();
  [...fields, mappings.director, mappings.source1, mappings.source2]
    .filter(Boolean)
    .forEach((field) => {
      const token = synergyCustomFieldToken(field);
      if (token) merged.set(token, field);
    });
  return Array.from(merged.values()).sort((a, b) =>
    `${a?.name || ''}`.localeCompare(`${b?.name || ''}`),
  );
};

function RestApiGrid({ comRow, setModalMode, setModalOpen, setSlug }) {
  const [selectSystem, setSelectSystem] = useState(-1);
  const [xeroAccounts, setXeroAccounts] = useState([]);
  const [sqlAccounts, setSqlAccounts] = useState([]);
  const [myobAccounts, setMyobAccounts] = useState([]);
  const [rapportAccounts, setRapportAccounts] = useState([]);
  const [workflowAccounts, setWorkflowAccounts] = useState([]);
  const [manualEntryAccounts, setManualEntryAccounts] = useState([]);
  const [synergyAccounts, setSynergyAccounts] = useState([]);
  const [synergyProjectCostBasis, setSynergyProjectCostBasis] = useState('transaction_date');
  const [synergyProjectCustomFieldMappings, setSynergyProjectCustomFieldMappings] = useState(emptySynergyProjectMappings);
  const [synergyProjectCustomFields, setSynergyProjectCustomFields] = useState([]);
  const [synergyCustomFieldsLoading, setSynergyCustomFieldsLoading] = useState(false);
  const [synergySettingSaving, setSynergySettingSaving] = useState(false);
  const [deltekVpAccounts, setDeltekVpAccounts] = useState([]);
  const [simplifyAccounts, setSimplifyAccounts] = useState([]);
  const [deltekSettingOpen, setDeltekSettingOpen] = useState(false);
  const [simplifySettingOpen, setSimplifySettingOpen] = useState(false);
  const [simplifyLoading, setSimplifyLoading] = useState(false);
  const [simplifyConnectLoading, setSimplifyConnectLoading] = useState(false);
  const [simplifySetting, setSimplifySetting] = useState({
    baseUrl: '',
    simplifyCompanyId: '',
    simplifyCompanyOrgId: '',
    clientId: '',
    clientSecret: '',
  });
  const [simplifySettingError, setSimplifySettingError] = useState({
    baseUrl: false,
    simplifyCompanyId: false,
    simplifyCompanyOrgId: false,
    clientId: false,
    clientSecret: false,
  });
  const [deltekSetting, setDeltekSetting] = useState({
    consumerKey: '',
    secretKey: '',
    dbName: '',
    siteUrl: '',
  });
  const [deltekSettingError, setDeltekSettingError] = useState({
    consumerKey: false,
    secretKey: false,
    dbName: false,
    siteUrl: false,
  });
  const [deltekDialogOpen, setDeltekDialogOpen] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [deltekPassword, setDeltekPassword] = useState('');
  const [deltekLoading, setDeltekLoading] = useState(false);
  const [showDeltekPassword, setShowDeltekPassword] = useState(false);
  const [deltekLoginError, setDeltekLoginError] = useState({
    customerId: false,
    deltekPassword: false,
  });

  useEffect(() => {
    const asyncFn = async () => {
      await getCompanyApiKeys(comRow.id);
    };
    asyncFn();
  }, [comRow]);

  const getCompanyApiKeys = (com_id) => {
    appdata.setItem('company_org_id', com_id);
    DefaultApi
      .getAllCompanyApiKeys(com_id)
      .then((res) => {
        if (res.status === 200) {
          let api_keys = res.data;
          for (let i = 0; i < api_keys.length; i++) {
            if (api_keys[i].ApiType === 'Xero') {
              setXeroAccounts(
                JSON.parse(api_keys[i].ApiAccounts)
                  ? JSON.parse(api_keys[i].ApiAccounts)
                  : [],
              );
            } else if (api_keys[i].ApiType === 'Sql') {
              setSqlAccounts(
                JSON.parse(api_keys[i].ApiAccounts)
                  ? JSON.parse(api_keys[i].ApiAccounts)
                  : [],
              );
            } else if (api_keys[i].ApiType === 'Myob') {
              setMyobAccounts(
                JSON.parse(api_keys[i].ApiAccounts)
                  ? JSON.parse(api_keys[i].ApiAccounts)
                  : [],
              );
            } else if (api_keys[i].ApiType === 'Rapport') {
              setRapportAccounts(
                JSON.parse(api_keys[i].ApiAccounts)
                  ? JSON.parse(api_keys[i].ApiAccounts)
                  : [],
              );
            } else if (api_keys[i].ApiType === 'Workflow Max') {
              setWorkflowAccounts(
                JSON.parse(api_keys[i].ApiAccounts)
                  ? JSON.parse(api_keys[i].ApiAccounts)
                  : [],
              );
            } else if (api_keys[i].ApiType === 'Manual Entry') {
              setManualEntryAccounts(
                JSON.parse(api_keys[i].ApiAccounts)
                  ? JSON.parse(api_keys[i].ApiAccounts)
                  : [],
              );
            } else if (api_keys[i].ApiType === 'Synergy') {
              setSlug(api_keys[i].Slug);
              setSynergyAccounts(
                JSON.parse(api_keys[i].ApiAccounts)
                  ? JSON.parse(api_keys[i].ApiAccounts)
                  : [],
              );
              try {
                const synergyParams = api_keys[i].StoreParams
                  ? JSON.parse(api_keys[i].StoreParams)
                  : {};
                setSynergyProjectCostBasis(
                  synergyParams?.projectCostBasis === 'job_cost'
                    ? 'job_cost'
                    : 'transaction_date',
                );
                const mappings = {
                  ...emptySynergyProjectMappings,
                  ...(synergyParams?.projectCustomFieldMappings || {}),
                };
                setSynergyProjectCustomFieldMappings(mappings);
                setSynergyProjectCustomFields((current) =>
                  mergeSynergyCustomFieldOptions(current, mappings),
                );
              } catch (error) {
                setSynergyProjectCostBasis('transaction_date');
                setSynergyProjectCustomFieldMappings(emptySynergyProjectMappings);
              }
            } else if (api_keys[i].ApiType === 'Deltek') {
              setDeltekVpAccounts(
                JSON.parse(api_keys[i].ApiAccounts)
                  ? JSON.parse(api_keys[i].ApiAccounts)
                  : [],
              );
            } else if (api_keys[i].ApiType === 'Simplify') {
              setSimplifyAccounts(
                JSON.parse(api_keys[i].ApiAccounts)
                  ? JSON.parse(api_keys[i].ApiAccounts)
                  : [],
              );
            }
          }
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleSystemToggle = (value) => {
    if (selectSystem !== value) {
      setSelectSystem(value);

      if (value === 7) {
        manageReportApi.getDeltekSetting()
          .then((res) => {
            if (res.status === 200) {
              if (res.data) {
                setDeltekSetting({
                  consumerKey: res.data.ConsumerKey,
                  secretKey: res.data.SecretKey,
                  dbName: res.data.DbName,
                  siteUrl: res.data.SiteUrl,
                });
              } else {
                setDeltekSetting({
                  consumerKey: '',
                  secretKey: '',
                  dbName: '',
                  siteUrl: '',
                });
              }
            } else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message));
      }

      if (value === 8) {
        manageReportApi.getSimplifySetting()
          .then((res) => {
            if (res.status === 200 && res.data) {
              setSimplifySetting({
                baseUrl: res.data.baseUrl || '',
                simplifyCompanyId: res.data.simplifyCompanyId || '',
                simplifyCompanyOrgId: res.data.simplifyCompanyOrgId || '',
                clientId: res.data.clientId || '',
                clientSecret: '',
              });
            } else if (res.status !== 200) {
              toast.error(res.message);
            }
          })
          .catch((error) => toast.error(error.message));
      }
    }
  };

  const handleSystemAccountCheck = (value) => {
    if (selectSystem === 0) {
      const currentIndex = xeroAccounts.indexOf(value);
      const newChecked = [...xeroAccounts];

      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);

      setXeroAccounts(newChecked);
    } else if (selectSystem === 1) {
      const currentIndex = sqlAccounts.indexOf(value);
      const newChecked = [...sqlAccounts];

      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);

      setSqlAccounts(newChecked);
    } else if (selectSystem === 2) {
      const currentIndex = myobAccounts.indexOf(value);
      const newChecked = [...myobAccounts];

      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);

      setMyobAccounts(newChecked);
    } else if (selectSystem === 3) {
      const currentIndex = rapportAccounts.indexOf(value);
      const newChecked = [...rapportAccounts];

      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);

      setRapportAccounts(newChecked);
    } else if (selectSystem === 4) {
      const currentIndex = workflowAccounts.indexOf(value);
      const newChecked = [...workflowAccounts];

      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);

      setWorkflowAccounts(newChecked);
    } else if (selectSystem === 5) {
      const currentIndex = manualEntryAccounts.indexOf(value);
      const newChecked = [...manualEntryAccounts];

      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);

      setManualEntryAccounts(newChecked);
    } else if (selectSystem === 6) {
      const currentIndex = synergyAccounts.indexOf(value);
      const newChecked = [...synergyAccounts];

      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);

      setSynergyAccounts(newChecked);
    } else if (selectSystem === 7) {
      const currentIndex = deltekVpAccounts.indexOf(value);
      const newChecked = [...deltekVpAccounts];

      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);

      setDeltekVpAccounts(newChecked);
    } else if (selectSystem === 8) {
      const currentIndex = simplifyAccounts.indexOf(value);
      const newChecked = [...simplifyAccounts];

      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);

      setSimplifyAccounts(newChecked);
    }
  };

  const handleSaveSimplifySetting = () => {
    const nextErrors = {
      baseUrl: simplifySetting.baseUrl === '',
      simplifyCompanyId: simplifySetting.simplifyCompanyId === '',
      simplifyCompanyOrgId: simplifySetting.simplifyCompanyOrgId === '',
      clientId: simplifySetting.clientId === '',
      clientSecret: simplifySetting.clientSecret === '',
    };

    if (Object.values(nextErrors).some(Boolean)) {
      setSimplifySettingError(nextErrors);
      return;
    }

    setSimplifyLoading(true);
    manageReportApi.updateSimplifySetting({
      baseUrl: simplifySetting.baseUrl,
      simplifyCompanyId: simplifySetting.simplifyCompanyId,
      simplifyCompanyOrgId: simplifySetting.simplifyCompanyOrgId,
      clientId: simplifySetting.clientId,
      clientSecret: simplifySetting.clientSecret,
    })
      .then((res) => {
        if (res.status === 200) {
          toast.success('Simplify setting saved successfully!');
          setSimplifySettingOpen(false);
          setSimplifySetting({ ...simplifySetting, clientSecret: '' });
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message))
      .finally(() => setSimplifyLoading(false));
  };

  const handleSaveDeltekSetting = () => {
    if (deltekSetting.consumerKey === '' || deltekSetting.secretKey === '' || deltekSetting.dbName === '' || deltekSetting.siteUrl === '') {
      setDeltekSettingError({
        consumerKey: deltekSetting.consumerKey === '',
        secretKey: deltekSetting.secretKey === '',
        dbName: deltekSetting.dbName === '',
        siteUrl: deltekSetting.siteUrl === '',
      });
      return;
    }

    setDeltekLoading(true);
    manageReportApi.updateDeltekSetting({
      ConsumerKey: deltekSetting.consumerKey,
      SecretKey: deltekSetting.secretKey,
      DbName: deltekSetting.dbName,
      SiteUrl: deltekSetting.siteUrl,
    })
      .then((res) => {
        if (res.status === 200) {
          toast.success('Deltek setting saved successfully!');
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message))
      .finally(() => {
        setDeltekLoading(false);
        setDeltekSettingOpen(false);
      });
  };

  const handleLoadSynergyCustomFields = () => {
    setSynergyCustomFieldsLoading(true);
    manageReportApi
      .getSynergyProjectCustomFields(comRow.id)
      .then((res) => {
        if (res.status === 200) {
          const fields = res?.data?.fields || [];
          setSynergyProjectCustomFields(
            mergeSynergyCustomFieldOptions(fields, synergyProjectCustomFieldMappings),
          );
          toast.success(`Loaded ${fields.length} Synergy project custom field${fields.length === 1 ? '' : 's'}`);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message))
      .finally(() => setSynergyCustomFieldsLoading(false));
  };

  const handleSynergyCustomFieldMappingChange = (target, token) => {
    const mapping = synergyProjectCustomFields.find(
      (field) => synergyCustomFieldToken(field) === token,
    ) || null;
    setSynergyProjectCustomFieldMappings((current) => ({
      ...current,
      [target]: mapping,
    }));
  };

  const handleSaveSynergySetting = () => {
    setSynergySettingSaving(true);
    DefaultApi
      .updateCompanyApiKey({
        companyKey: comRow.id,
        projectCostBasis: synergyProjectCostBasis,
        projectCustomFieldMappings: synergyProjectCustomFieldMappings,
      })
      .then((res) => {
        if (res.status === 200) {
          toast.success('Synergy project settings saved');
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message))
      .finally(() => setSynergySettingSaving(false));
  };

  const getApiAccountsPayload = () => ({
    ['Xero']: xeroAccounts,
    ['Sql']: sqlAccounts,
    ['Myob']: myobAccounts,
    ['Rapport']: rapportAccounts,
    ['Workflow Max']: workflowAccounts,
    ['Manual Entry']: manualEntryAccounts,
    ['Synergy']: synergyAccounts,
    ['Deltek']: deltekVpAccounts,
    ['Simplify']: simplifyAccounts,
  });

  const persistApiAccounts = () => DefaultApi.addCompanyApiKey(
    comRow.id,
    getApiAccountsPayload(),
  );

  const handleRestSDKSave = (type) => {
    persistApiAccounts()
      .then((res) => {
        if (res.status === 200) {
          toast.success(`${type} account selections saved`);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleRestSDKGo = (type) => {
    let apiAccounts = [];

    switch(type) {
      case 'Xero':
        apiAccounts = xeroAccounts;
        break;
      case 'Sql':
        apiAccounts = sqlAccounts;
        break;
      case 'Myob':
        apiAccounts = myobAccounts;
        break;
      case 'Rapport':
        apiAccounts = rapportAccounts;
        break;
      case 'Workflow Max':
        apiAccounts = workflowAccounts;
        break;
      case 'Manual Entry':
        apiAccounts = manualEntryAccounts;
        break;
      case 'Synergy':
        apiAccounts = synergyAccounts;
        break;
      case 'Deltek':
        apiAccounts = deltekVpAccounts;
        break;
      case 'Simplify':
        apiAccounts = simplifyAccounts;
        break;
      default:
        return;
    }

    if (apiAccounts.length === 0) {
      toast.warning('Please select the any account type!');
      return;
    }

    if (type === 'Simplify') {
      if (simplifySetting.baseUrl === '' || simplifySetting.simplifyCompanyId === '' || simplifySetting.simplifyCompanyOrgId === '' || simplifySetting.clientId === '') {
        toast.warning('Please fill the simplify setting and save it first!');
        return;
      }
    }

    if (selectSystem === 7) {
      if (deltekSetting.consumerKey === '' || deltekSetting.secretKey === '' || deltekSetting.dbName === '' || deltekSetting.siteUrl === '') {
        toast.warning('Please fill the deltek setting!');
        return;
      }
    }

    if (type === 'Simplify') setSimplifyConnectLoading(true);

    persistApiAccounts()
      .then(async (res) => {
        if (res.status === 200) {
          if (type === 'Deltek') {
            setDeltekDialogOpen(true);
          } else if (type === 'Simplify') {
            const connection = await manageReportApi.checkSimplifyConnection(comRow.id);
            if (connection.status === 200 && connection.connected) {
              toast.success('Simplify connected successfully!');
            } else {
              toast.error(connection.message || 'R4D could not connect to Simplify.');
            }
          } else {
            if (type === 'Synergy') {
              localStorage.setItem('synergy_company', comRow.id);
            }
            let redirect = await manageReportApi.oauth(type);
            let redirectURL = redirect.url;
            window.location.href = redirectURL;
          }
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message))
      .finally(() => {
        if (type === 'Simplify') setSimplifyConnectLoading(false);
      });
  };

  const handleDeltekLogin = () => {
    if (customerId === '' || deltekPassword === '') {
      setDeltekLoginError({
        customerId: customerId === '',
        deltekPassword: deltekPassword === '',
      });
      return;
    }

    setDeltekLoading(true);
    manageReportApi.deltekOauth(customerId, deltekPassword)
      .then((res) => {
        if (res.status === 200) {
          toast.success('Deltek login successful!');
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message))
      .finally(() => {
        setDeltekLoading(false);
        setDeltekDialogOpen(false);
        setCustomerId('');
        setDeltekPassword('');
      });
  };

  const handleCloseDeltekDialog = () => {
    setDeltekDialogOpen(false);
    setCustomerId('');
    setDeltekPassword('');
  };

  return (
    <Grid container columns={19} spacing={1} sx={{ height: '50%' }}>
      <Grid size={9} sx={{ overflow: 'auto', height: '100%' }}>
        <List>
          {systemLists.map((system, index) => (
            <ListItemButton
              key={index}
              onClick={() => handleSystemToggle(index)}
            >
              <ListItemIcon>
                <img
                  alt={system.text}
                  src={system.src}
                  style={{
                    height: 50,
                    opacity: index === selectSystem ? 1 : 0.2,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={system.text}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  '& .MuiTypography-displayBlock': {
                    fontSize: 20,
                  },
                }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="comments"
                  onClick={() => handleSystemToggle(index)}
                >
                  {selectSystem === index ? (
                    <ArrowBackIosIcon />
                  ) : (
                    <ArrowForwardIosIcon />
                  )}
                </IconButton>
              </ListItemSecondaryAction>
            </ListItemButton>
          ))}
        </List>
      </Grid>
      <Grid size={10} sx={{ overflow: 'auto', height: '100%' }}>
        {selectSystem === 0 ? (
          <FormControl
            component="fieldset"
            sx={{
              padding: '8px 16px',
              width: '100%',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <FormLabel
                component="legend"
                sx={{ padding: '8px 0', fontSize: '20px', color: 'black' }}
              >
                Xero Account List
              </FormLabel>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => handleRestSDKSave('Xero')}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SettingsEthernetIcon fontSize="small" />}
                  color="primary"
                  size="small"
                  onClick={() => handleRestSDKGo('Xero')}
                  disabled={xeroAccounts.length === 0}
                >
                  Connect
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid size={6}>
                {xeroAccountLists.map((list, index) =>
                  index < 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={xeroAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
              <Grid size={6}>
                {xeroAccountLists.map((list, index) =>
                  index >= 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={xeroAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
            </Grid>            
          </FormControl>
        ) : null}
        {selectSystem === 1 ? (
          <FormControl
            component="fieldset"
            sx={{
              padding: '8px 16px',
              width: '100%',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <FormLabel
                component="legend"
                sx={{ padding: '8px 0', fontSize: '20px', color: 'black' }}
              >
                Sql Account List
              </FormLabel>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => handleRestSDKSave('Sql')}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SettingsEthernetIcon fontSize="small" />}
                  color="primary"
                  size="small"
                  onClick={() => handleRestSDKGo('Sql')}
                  disabled={sqlAccounts.length === 0}
                >
                  Connect
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid size={6}>
                {sqlAccountLists.map((list, index) =>
                  index < 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={sqlAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
              <Grid size={6}>
                {sqlAccountLists.map((list, index) =>
                  index >= 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={sqlAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
            </Grid>
          </FormControl>
        ) : null}
        {selectSystem === 2 ? (
          <FormControl
            component="fieldset"
            sx={{
              padding: '8px 16px',
              width: '100%',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <FormLabel
                component="legend"
                sx={{ padding: '8px 0', fontSize: '20px', color: 'black' }}
              >
                Myob Account List
              </FormLabel>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => handleRestSDKSave('Myob')}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SettingsEthernetIcon fontSize="small" />}
                  color="primary"
                  size="small"
                  onClick={() => handleRestSDKGo('Myob')}
                  disabled={myobAccounts.length === 0}
                >
                  Connect
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid size={6}>
                {myobAccountLists.map((list, index) =>
                  index < 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={myobAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
              <Grid size={6}>
                {myobAccountLists.map((list, index) =>
                  index >= 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={myobAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
            </Grid>            
          </FormControl>
        ) : null}
        {selectSystem === 3 ? (
          <FormControl
            component="fieldset"
            sx={{
              padding: '8px 16px',
              width: '100%',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <FormLabel
                component="legend"
                sx={{ padding: '8px 0', fontSize: '20px', color: 'black' }}
              >
                Rapport Account List
              </FormLabel>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => handleRestSDKSave('Rapport')}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SettingsEthernetIcon fontSize="small" />}
                  color="primary"
                  size="small"
                  onClick={() => handleRestSDKGo('Rapport')}
                  disabled={rapportAccounts.length === 0}
                >
                  Connect
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid size={6}>
                {rapportAccountLists.map((list, index) =>
                  index < 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={rapportAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
              <Grid size={6}>
                {rapportAccountLists.map((list, index) =>
                  index >= 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={rapportAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
            </Grid>
          </FormControl>
        ) : null}
        {selectSystem === 4 ? (
          <FormControl
            component="fieldset"
            sx={{
              padding: '8px 16px',
              width: '100%',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <FormLabel
                component="legend"
                sx={{ padding: '8px 0', fontSize: '20px', color: 'black' }}
              >
                Workflow Account List
              </FormLabel>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => handleRestSDKSave('Workflow Max')}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SettingsEthernetIcon fontSize="small" />}
                  color="primary"
                  size="small"
                  onClick={() => handleRestSDKGo('Workflow Max')}
                  disabled={workflowAccounts.length === 0}
                >
                  Connect
                </Button>
              </Box>
            </Box>
            {workflowAccountLists.map((list, index) => (
              <FormControlLabel
                key={index}
                disabled={
                  sqlAccounts.filter((account) => account === list).length >
                    0 ||
                  xeroAccounts.filter((account) => account === list).length >
                    0 ||
                  myobAccounts.filter((account) => account === list).length >
                    0 ||
                  rapportAccounts.filter((account) => account === list).length >
                    0 ||
                  manualEntryAccounts.filter((account) => account === list)
                    .length > 0 ||
                  synergyAccounts.filter((account) => account === list).length >
                    0 ||
                  deltekVpAccounts.filter((account) => account === list)
                    .length > 0
                    ? true
                    : false
                }
                control={
                  <Checkbox
                    checked={workflowAccounts.indexOf(list) !== -1}
                    onChange={() => handleSystemAccountCheck(list)}
                    name={list}
                    color="success"
                  />
                }
                label={list}
              />
            ))}            
          </FormControl>
        ) : null}
        {selectSystem === 5 ? (
          <FormControl
            component="fieldset"
            sx={{
              padding: '8px 16px',
              width: '100%',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <FormLabel
                component="legend"
                sx={{ padding: '8px 0', fontSize: '20px', color: 'black' }}
              >
                Manual Entry Account List
              </FormLabel>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => handleRestSDKSave('Manual Entry')}
              >
                Save
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid size={6}>
                {manaualEntryAccountLists.map((list, index) =>
                  index < 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={manualEntryAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
              <Grid size={6}>
                {manaualEntryAccountLists.map((list, index) =>
                  index >= 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={manualEntryAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
            </Grid>
          </FormControl>
        ) : null}
        {selectSystem === 6 ? (
          <FormControl
            component="fieldset"
            sx={{
              padding: '8px 16px',
              width: '100%',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <FormLabel
                component="legend"
                sx={{ padding: '8px 0', fontSize: '20px', color: 'black' }}
              >
                Synergy Account List
              </FormLabel>
              <Box display="flex" gap={1}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setModalMode('slug');
                    setModalOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => handleRestSDKSave('Synergy')}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SettingsEthernetIcon fontSize="small" />}
                  color="primary"
                  size="small"
                  onClick={() => handleRestSDKGo('Synergy')}
                  disabled={synergyAccounts.length === 0}
                >
                  Connect
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid size={6}>
                {synergyAccountLists.map((list, index) =>
                  index < 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={synergyAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
              <Grid size={6}>
                {synergyAccountLists.map((list, index) =>
                  index >= 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        deltekVpAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={synergyAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
            </Grid>
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
                <TextField
                  select
                  size="small"
                  label="Project cost basis"
                  value={synergyProjectCostBasis}
                  onChange={(event) => setSynergyProjectCostBasis(event.target.value)}
                  sx={{ minWidth: 310 }}
                  helperText={
                    synergyProjectCostBasis === 'job_cost'
                      ? 'Use for Total Synergy Job Cost reporting (invoice/write-off date).'
                      : 'Legacy Actual Cost behaviour, including WIP on transaction date.'
                  }
                >
                  <MenuItem value="transaction_date">
                    Transaction date / Actual Cost (legacy)
                  </MenuItem>
                  <MenuItem value="job_cost">
                    Invoice/write-off date / Job Cost
                  </MenuItem>
                </TextField>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleLoadSynergyCustomFields}
                  disabled={synergyCustomFieldsLoading || synergyAccounts.length === 0}
                  sx={{ mt: 0.5 }}
                >
                  {synergyCustomFieldsLoading ? 'Loading fields…' : 'Load custom fields'}
                </Button>
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormLabel sx={{ color: 'text.primary', fontSize: 14, fontWeight: 600 }}>
                  Project custom field mapping
                </FormLabel>
                <Box sx={{ mt: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {[
                    ['director', 'Director'],
                    ['source1', 'Project Source 1'],
                    ['source2', 'Project Source 2'],
                  ].map(([target, label]) => (
                    <TextField
                      key={target}
                      select
                      size="small"
                      label={label}
                      value={synergyCustomFieldToken(synergyProjectCustomFieldMappings[target])}
                      onChange={(event) =>
                        handleSynergyCustomFieldMappingChange(target, event.target.value)
                      }
                      sx={{ minWidth: 220 }}
                      helperText={target === 'director'
                        ? 'Blank keeps the standard Synergy Manager → Director behaviour.'
                        : 'Blank leaves the R4D source field unchanged.'}
                    >
                      <MenuItem value="">
                        {target === 'director' ? 'Use Synergy Manager (default)' : 'Not mapped'}
                      </MenuItem>
                      {synergyProjectCustomFields.map((field) => (
                        <MenuItem
                          key={`${target}-${synergyCustomFieldToken(field)}`}
                          value={synergyCustomFieldToken(field)}
                        >
                          {field.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  ))}
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSaveSynergySetting}
                  disabled={synergySettingSaving}
                >
                  {synergySettingSaving ? 'Saving…' : 'Save Synergy settings'}
                </Button>
              </Box>
            </Box>
          </FormControl>
        ) : null}
        {selectSystem === 7 ? (
          <FormControl
            component="fieldset"
            sx={{
              padding: '8px 16px',
              width: '100%',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <FormLabel
                component="legend"
                sx={{ padding: '8px 0', fontSize: '20px', color: 'black' }}
              >
                Deltek Account List
              </FormLabel>
              <Box display="flex" gap={1}>
                <IconButton
                  size="small"
                  onClick={() => setDeltekSettingOpen(true)}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => handleRestSDKSave('Deltek')}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SettingsEthernetIcon fontSize="small" />}
                  color="primary"
                  size="small"
                  onClick={() => handleRestSDKGo('Deltek')}
                  disabled={deltekVpAccounts.length === 0 || deltekDialogOpen}
                >
                  Connect
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid size={6}>
                {deltekVpAccountLists.map((list, index) =>
                  index < 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={deltekVpAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
              <Grid size={6}>
                {deltekVpAccountLists.map((list, index) =>
                  index >= 7 ? (
                    <FormControlLabel
                      key={index}
                      style={{ width: '100%' }}
                      disabled={
                        xeroAccounts.filter((account) => account === list)
                          .length > 0 ||
                        sqlAccounts.filter((account) => account === list)
                          .length > 0 ||
                        myobAccounts.filter((account) => account === list)
                          .length > 0 ||
                        rapportAccounts.filter((account) => account === list)
                          .length > 0 ||
                        workflowAccounts.filter((account) => account === list)
                          .length > 0 ||
                        manualEntryAccounts.filter(
                          (account) => account === list,
                        ).length > 0 ||
                        synergyAccounts.filter((account) => account === list)
                          .length > 0
                          ? true
                          : false
                      }
                      control={
                        <Checkbox
                          checked={deltekVpAccounts.indexOf(list) !== -1}
                          onChange={() => handleSystemAccountCheck(list)}
                          name={list}
                          color="success"
                        />
                      }
                      label={list}
                    />
                  ) : null,
                )}
              </Grid>
            </Grid>            
          </FormControl>
        ) : null}

        {selectSystem === 8 ? (
          <FormControl
            component="fieldset"
            sx={{
              padding: '8px 16px',
              width: '100%',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <FormLabel
                component="legend"
                sx={{ padding: '8px 0', fontSize: '20px', color: 'black' }}
              >
                Simplify Account List
              </FormLabel>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton onClick={() => setSimplifySettingOpen(true)}>
                  <SettingsIcon />
                </IconButton>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => handleRestSDKSave('Simplify')}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  disabled={simplifyConnectLoading || simplifyAccounts.length === 0}
                  onClick={() => handleRestSDKGo('Simplify')}
                >
                  {simplifyConnectLoading ? 'Connecting...' : 'Connect'}
                </Button>
              </Box>
            </Box>
            {simplifyAccountLists.map((list, index) => (
              <FormControlLabel
                key={index}
                label={list}
                control={
                  <Checkbox
                    checked={simplifyAccounts.filter((account) => account === list).length !== 0}
                    onChange={() => handleSystemAccountCheck(list)}
                  />
                }
              />
            ))}
          </FormControl>
        ) : null}
      </Grid>


      <Dialog maxWidth="xs" open={simplifySettingOpen} onClose={() => setSimplifySettingOpen(false)}>
        <DialogTitle>Simplify Settings</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={1} pt={1} flexDirection="column" sx={{ width: '350px' }}>
            <TextField
              label="Simplify API Base URL"
              value={simplifySetting.baseUrl}
              onChange={(e) => setSimplifySetting({ ...simplifySetting, baseUrl: e.target.value })}
              margin="normal"
              error={simplifySettingError.baseUrl}
              helperText={simplifySettingError.baseUrl ? 'Base URL is required' : 'Example: https://your-simplify-host/api'}
            />
            <TextField
              label="Simplify Company ID"
              value={simplifySetting.simplifyCompanyId}
              onChange={(e) => setSimplifySetting({ ...simplifySetting, simplifyCompanyId: e.target.value })}
              margin="normal"
              error={simplifySettingError.simplifyCompanyId}
              helperText={simplifySettingError.simplifyCompanyId ? 'Company ID is required' : ''}
            />
            <TextField
              label="Simplify Company Org ID"
              value={simplifySetting.simplifyCompanyOrgId}
              onChange={(e) => setSimplifySetting({ ...simplifySetting, simplifyCompanyOrgId: e.target.value })}
              margin="normal"
              error={simplifySettingError.simplifyCompanyOrgId}
              helperText={simplifySettingError.simplifyCompanyOrgId ? 'Company Org ID is required' : ''}
            />
            <TextField
              label="Client ID"
              value={simplifySetting.clientId}
              onChange={(e) => setSimplifySetting({ ...simplifySetting, clientId: e.target.value })}
              margin="normal"
              error={simplifySettingError.clientId}
              helperText={simplifySettingError.clientId ? 'Client ID is required' : ''}
            />
            <TextField
              label="Client Secret"
              type="password"
              value={simplifySetting.clientSecret}
              onChange={(e) => setSimplifySetting({ ...simplifySetting, clientSecret: e.target.value })}
              margin="normal"
              error={simplifySettingError.clientSecret}
              helperText={simplifySettingError.clientSecret ? 'Client Secret is required' : 'Paste the secret generated in Simplify'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="default" onClick={() => setSimplifySettingOpen(false)}>Cancel</Button>
          <Button color="success" loading={simplifyLoading} loadingPosition="start" onClick={() => handleSaveSimplifySetting()}>Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog maxWidth="xs" open={deltekSettingOpen} onClose={() => setDeltekSettingOpen(false)}>
        <DialogTitle>Deltek Settings</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={1} pt={1} flexDirection="column" sx={{ width: '350px' }}>
            <TextField
              label="Consumer Key"
              value={deltekSetting.consumerKey}
              onChange={(e) => setDeltekSetting({ ...deltekSetting, consumerKey: e.target.value })}
              margin="normal"
              error={deltekSettingError.consumerKey}
              helperText={deltekSettingError.consumerKey ? 'Consumer Key is required' : ''}
            />
            <TextField
              label="Secret Key"
              value={deltekSetting.secretKey}
              onChange={(e) => setDeltekSetting({ ...deltekSetting, secretKey: e.target.value })}
              margin="normal"
              error={deltekSettingError.secretKey}
              helperText={deltekSettingError.secretKey ? 'Secret Key is required' : ''}
            />
            <TextField
              label="Database Name"
              value={deltekSetting.dbName}
              onChange={(e) => setDeltekSetting({ ...deltekSetting, dbName: e.target.value })}
              margin="normal"
              error={deltekSettingError.dbName}
              helperText={deltekSettingError.dbName ? 'Database Name is required' : ''}
            />
            <TextField
              label="Site URL"
              value={deltekSetting.siteUrl}
              onChange={(e) => setDeltekSetting({ ...deltekSetting, siteUrl: e.target.value })}
              margin="normal"
              error={deltekSettingError.siteUrl}
              helperText={deltekSettingError.siteUrl ? 'Site URL is required' : ''}
            />            
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="default" onClick={() => setDeltekSettingOpen(false)}>Cancel</Button>
          <Button color="success" loading={deltekLoading} loadingPosition="start" onClick={() => handleSaveDeltekSetting()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deltekDialogOpen} onClose={handleCloseDeltekDialog}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src={deltek_vp_logo} alt="Deltek Logo" style={{ width: '22px', height: '22px' }} />
          Deltek Vantagepoint Login
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Customer ID"
            value={customerId}
            onChange={e => setCustomerId(e.target.value)}
            fullWidth
            margin="normal"
            disabled={deltekLoading}
            error={deltekLoginError.customerId}
            helperText={deltekLoginError.customerId ? 'Customer ID is required' : ''}
          />
          <TextField
            label="Password"
            type={showDeltekPassword ? 'text' : 'password'}
            value={deltekPassword}
            onChange={e => setDeltekPassword(e.target.value)}
            fullWidth
            margin="normal"
            disabled={deltekLoading}
            error={deltekLoginError.deltekPassword}
            helperText={deltekLoginError.deltekPassword ? 'Password is required' : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowDeltekPassword(!showDeltekPassword)}
                    edge="end"
                  >
                    {showDeltekPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button color="default" onClick={handleCloseDeltekDialog}>Cancel</Button>
          <Button loading={deltekLoading} color="success" loadingPosition="start" onClick={handleDeltekLogin}>Log In</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

RestApiGrid.propTypes = {
  comRow: PropTypes.object.isRequired,
  setModalMode: PropTypes.func.isRequired,
  setModalOpen: PropTypes.func.isRequired,
  setSlug: PropTypes.func.isRequired,
};

export default RestApiGrid;
